import { useState } from "react";
import { Modal } from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import { bouncy } from "ldrs";
import { toast } from "react-toastify";
import { addDriverTSMap, updateDriverTSMap } from "../Attendance/AttendanceApis";

bouncy.register();

const AddEditDriverTsMap = ({
    showModal,
    setShowModal,
    editRow,
    isEditMode,
    driverData,
    tsData,
    getDriverTSMapData,
}) => {
    const [loading, setLoading] = useState(false);

    const handleClose = () => {
        setShowModal(false);
    };

    const initialValues = {
        driverId: isEditMode ? editRow?.driverId : "",
        transferStationId: isEditMode ? (editRow?.transferStationId || editRow?.tsId) : "",
        isActive: isEditMode ? editRow?.isActive?.toString() : "true",
    };

    const validationSchema = Yup.object({
        driverId: Yup.string().required("Driver is required"),
        transferStationId: Yup.string().required("Transfer Station is required"),
        ...(isEditMode && {
            isActive: Yup.string().required("Status is required"),
        }),
    });

    const handleSubmit = async (values) => {
        const payload = {
            driverId: Number(values.driverId),
            transferStationId: Number(values.transferStationId),
        };

        setLoading(true);

        try {
            if (isEditMode) {
                const response = await updateDriverTSMap({
                    driverTSMapId: editRow?.driverTSMapId,
                    driverId: Number(values.driverId),
                    transferStationId: Number(values.transferStationId),
                    isActive: values.isActive === "true",
                });

                if (response.status === 200) {
                    toast.success("Driver TS Map updated successfully!");
                    getDriverTSMapData();
                    handleClose();
                } else if (response.status === 500) {
                    toast.error(response.response?.data?.title);
                } else {
                    toast.error(response.response?.data);
                }
            } else {
                const response = await addDriverTSMap(payload);

                if (response.status === 200) {
                    toast.success("Driver TS Map added successfully!");
                    getDriverTSMapData();
                    handleClose();
                } else {
                    toast.error(response.response?.data);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            show={showModal}
            onHide={handleClose}
            size="md"
            centered
        >
            <Modal.Header>
                <Modal.Title>
                    {isEditMode ? "Edit Driver TS Map" : "Add Driver TS Map"}
                </Modal.Title>
                <i
                    className="fa-solid fa-xmark close-container"
                    onClick={handleClose}
                ></i>
            </Modal.Header>

            <hr />

            <Modal.Body>
                <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    <FormikForm>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <label htmlFor="driverId" className="form-label">
                                        Driver <span className="text-danger">*</span>
                                    </label>
                                    <Field
                                        as="select"
                                        name="driverId"
                                        id="driverId"
                                        className="form-select"
                                    >
                                        <option value="">Select Driver</option>
                                        {driverData?.map((driver) => (
                                            <option
                                                key={driver.driverId}
                                                value={driver.driverId}
                                            >
                                                {driver.driverName}
                                            </option>
                                        ))}
                                    </Field>
                                    <ErrorMessage
                                        name="driverId"
                                        component="div"
                                        className="text-danger"
                                    />
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="form-group">
                                    <label htmlFor="transferStationId" className="form-label">
                                        Transfer Station <span className="text-danger">*</span>
                                    </label>
                                    <Field
                                        as="select"
                                        name="transferStationId"
                                        id="transferStationId"
                                        className="form-select"
                                    >
                                        <option value="">Select Transfer Station</option>
                                        {tsData?.map((ts) => (
                                            <option
                                                key={ts.transferStationId || ts.tsId}
                                                value={ts.transferStationId || ts.tsId}
                                            >
                                                {ts.transferStationName || ts.tsName}
                                            </option>
                                        ))}
                                    </Field>
                                    <ErrorMessage
                                        name="transferStationId"
                                        component="div"
                                        className="text-danger"
                                    />
                                </div>
                            </div>

                            {isEditMode && (
                                <div className="col-md-6 mt-3">
                                    <label className="form-label">
                                        Status <span className="text-danger">*</span>
                                    </label>
                                    <div className="form-group" style={{ display: "flex", alignItems: "center" }}>
                                        <div className="form-check d-flex">
                                            <Field
                                                type="radio"
                                                className="form-check-input"
                                                name="isActive"
                                                id="activeRadio"
                                                value="true"
                                            />
                                            <label className="form-check-label" htmlFor="activeRadio">
                                                Active
                                            </label>
                                        </div>
                                        <div className="form-check ms-4 d-flex">
                                            <Field
                                                type="radio"
                                                className="form-check-input"
                                                name="isActive"
                                                id="inactiveRadio"
                                                value="false"
                                            />
                                            <label className="form-check-label" htmlFor="inactiveRadio">
                                                InActive
                                            </label>
                                        </div>
                                    </div>
                                    <ErrorMessage
                                        name="isActive"
                                        component="div"
                                        className="text-danger"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="text-center mt-4">
                            <button
                                type="submit"
                                className="deployment-button"
                                disabled={loading}
                            >
                                {loading ? (
                                    <l-bouncy
                                        size="25"
                                        speed="1.75"
                                        color="white"
                                    ></l-bouncy>
                                ) : (
                                    <>
                                        <i className="fa-solid fa-check done-container"></i>{" "}
                                        {isEditMode ? "Update" : "Submit"}
                                    </>
                                )}
                            </button>
                        </div>
                    </FormikForm>
                </Formik>
            </Modal.Body>
        </Modal>
    );
};

export default AddEditDriverTsMap;
